import { CommonModule, NgOptimizedImage } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ProductDisplayStep } from '@digilize/shared/definitions/src';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { AppContext } from '@plys/src/application/app.context';
import { CartState } from '@plys/src/application/states/cart.state';
import { CheckoutState } from '@plys/src/application/states/checkout.state';
import { ShowState } from '@plys/src/application/states/show.state';
import { StateService } from '@plys/src/infrastructure/state-service.service';
import { hasConfirmedAge, isAgeRestricted, rememberAgeConfirmed, requiredAge } from '@plys/src/models/show';
import { CartPreviewComponent } from '@plys/src/ui/cart-preview/cart-preview.component';
import { CheckoutUIService } from '@plys/src/ui/checkout/checkout-u-i.service';
import { ConfirmDialogComponent } from '@plys/src/ui/confirm-dialog/confirm-dialog.component';
import { MobileCartFooterComponent } from '@plys/src/ui/mobile-cart-footer/mobile-cart-footer.component';
import { MovieWidgetComponent } from '@plys/src/ui/movie-widget/movie-widget.component';
import { ResizePipe } from '@plys/src/ui/media/resize.pipe';
import { QuantityControlComponent } from '@plys/src/ui/quantity-control/quantity-control.component';
import { InlineSVGModule } from 'ng-inline-svg-2';
import {
  catchError,
  combineLatest,
  filter,
  first,
  map,
  Observable,
  of,
  startWith,
  Subject,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs';
import {
  Product,
  ProductType,
  Supplement,
  TicketAvailabilityState,
  ticketAvailabilityState,
} from '../../models/product';
import { LocaleCurrencyPipe } from '../../ui/locale-currency.pipe';

export interface ProductViewData {
  product: Product;
  supplements: Supplement[];
  /** Decides whether the quantity counter is rendered at all, and whether + is stopped. */
  availabilityState: TicketAvailabilityState;
}

export interface GroupViewData {
  title: string;
  order: number;
  items: ProductViewData[];
}

@Component({
  standalone: true,
  imports: [
    LocaleCurrencyPipe,
    CommonModule,
    TranslocoModule,
    InlineSVGModule,
    NgOptimizedImage,
    MatProgressSpinnerModule,
    CartPreviewComponent,
    MobileCartFooterComponent,
    MovieWidgetComponent,
    QuantityControlComponent,
    ResizePipe,
  ],
  templateUrl: './tickets-page.component.html',
  styleUrls: ['./tickets-page.component.scss', '../../styles/list-item.scss'],
})
export class TicketsPageComponent implements OnInit, OnDestroy {
  readonly ticketProducts$ = this.getProductsWithSupplements();
  protected loading = true;
  private destroy$ = new Subject<void>();
  private ProductType = ProductType;
  protected readonly reserving$ = this.cartState.reserving$;
  readonly expandDetails: Record<string, boolean> = {};
  constructor(
    public showState: ShowState,
    private cartState: CartState,
    private checkoutState: CheckoutState,
    private checkout: CheckoutUIService,
    private stateService: StateService,
    private appContext: AppContext,
    private dialog: MatDialog,
    private transloco: TranslocoService
  ) {}
  ngOnInit(): void {
    /* we are not waiting for response to show products faster*/
    this.showState.fetchSupplements(this.appContext.productIds, ProductDisplayStep.ProductsPage).subscribe();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  addTicketToCart(product: Product): void {
    // No sold-out guard here: + is disabled for anything the availability snapshot rules out, and a
    // race against another buyer is caught by the reservation call itself.
    this.cartState.addTicketProduct(product).pipe(take(1), takeUntil(this.destroy$)).subscribe();
  }

  addItemToCart(item: Supplement, supplements: Supplement[], parentId = null): void {
    this.cartState
      .addItem(item, supplements, parentId)
      .pipe(
        first(),
        takeUntil(this.destroy$),
        catchError((err: Error) => {
          this.checkoutState.setError(err.message);
          return of(null);
        })
      )
      .subscribe();
  }

  removeTicketFromCart(id: string): void {
    this.cartState.removeTicketByProductId(id).pipe(first(), takeUntil(this.destroy$)).subscribe();
  }

  removeItemFromCart(id: string): void {
    this.cartState.removeItem(id).pipe(first(), takeUntil(this.destroy$)).subscribe();
  }

  getTicketQuantity(productId: string): Observable<number> {
    return this.cartState.getTicketQuantity(productId);
  }

  getProductsWithSupplements(): Observable<GroupViewData[]> {
    return combineLatest([
      this.showState.products$,
      this.showState.ticketPageSupplements$.pipe(startWith([])),
      this.cartState.tickets$,
    ]).pipe(
      map(([products, supplements, tickets]) => {
        const groupedProducts = products.reduce((acc, product) => {
          const productSupplements = supplements.filter((supplement) => product.id === supplement.mainProductId);
          const group = product.group?.title || 'Other';
          if (!acc[group]) {
            acc[group] = { title: group, order: product.group?.order || 0, items: [] };
          }
          const quantity = tickets.filter((ticket) => ticket.product?.id === product.id).length;
          acc[group].items.push({
            product: product,
            supplements: productSupplements,
            availabilityState: ticketAvailabilityState(product, quantity),
          });
          return acc;
        }, {} as Record<string, GroupViewData>);
        return Object.values(groupedProducts);
      }),
      map((groups) => {
        return groups
          .map((group) => {
            return {
              ...group,
              items: group.items.sort((a, b) => a.product.order - b.product.order),
            };
          })
          .sort((a, b) => a.order - b.order);
      }),
      tap((products) => (this.loading = !products))
    );
  }

  getItemMaxQuantity(supplement: Supplement): Observable<number> {
    return this.cartState.getItemMaxQuantity(supplement);
  }

  getSupplementQuantity(suppId: string, productId: string): Observable<number> {
    return this.cartState.getSupplementQuantity(suppId, productId);
  }

  hasItemsWithin(productId: string, supplements: Supplement[]): Observable<boolean> {
    return this.getTicketQuantity(productId).pipe(
      map((quantity) => quantity > 0),
      map((hasProductInCart) => hasProductInCart && supplements?.length > 0)
    );
  }

  public handleContinueClick(): void {
    this.confirmAgeIfRequired()
      .pipe(
        filter((proceed) => proceed),
        switchMap(() => this.cartState.requireReservation()),
        first(),
        tap((require) => {
          if (require) this.checkoutState.moveReservingStepAsNextStep(this.cartState.getCartIdValue());
        }),
        switchMap(() => this.stateService.tryToSkipReservingStep()),
        tap(() => {
          this.checkout.goNext(false);
        })
      )
      .subscribe();
  }

  /* Asked once per browser session so every order is backed by a confirmation from that buying session.
     The message names the movie's own rating, but the age the buyer confirms stays 16 for every rating:
     16 is the legally binding minimum, so 16-year-olds may see an 18-rated movie. */
  private confirmAgeIfRequired(): Observable<boolean> {
    const show = this.showState.show;
    if (!isAgeRestricted(show) || hasConfirmedAge()) return of(true);

    return this.dialog
      .open(ConfirmDialogComponent, {
        data: {
          title: this.transloco.translate('tickets.age_verification.title'),
          message: this.transloco.translate('tickets.age_verification.message', { age: requiredAge(show) }),
          confirmText: this.transloco.translate('tickets.age_verification.confirm'),
        },
      })
      .afterClosed()
      .pipe(
        map((result) => result === true),
        tap((confirmed) => confirmed && rememberAgeConfirmed())
      );
  }

  public toggleDetails(productId: string, supplementId: string = ''): void {
    this.expandDetails[productId + supplementId] = !this.expandDetails[productId + supplementId];
  }

  public isExpanded(productId: string, supplementId: string = ''): boolean {
    return this.expandDetails[productId + supplementId] || false;
  }

  public isMenuVisible(item: Product, supplements?: Supplement[]): boolean {
    return !supplements?.length && item.hasReservation;
  }

  get disabledButton$(): Observable<boolean> {
    return combineLatest([this.cartState.tickets$, this.reserving$]).pipe(
      map(([tickets, reserving]) => tickets?.length === 0 || reserving)
    );
  }
}
